"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState,
} from "react-hook-form";
import classnames from "classnames";

// Utility function to combine classnames (similar to cn utility)
const cn = (...classes) => {
  return classnames(...classes);
};

// Label component (simplified version for this project)
const Label = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <label
      ref={ref}
      className={cn(
        "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
        className
      )}
      {...props}
    />
  );
});
Label.displayName = "Label";

const Form = FormProvider;

const FormFieldContext = React.createContext({});

const FormField = React.forwardRef(({ ...props }, ref) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
});
FormField.displayName = "FormField";

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

const FormItemContext = React.createContext({});

const FormItem = React.forwardRef(({ className, ...props }, ref) => {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div
        ref={ref}
        data-slot="form-item"
        className={cn("grid gap-2", className)}
        {...props}
      />
    </FormItemContext.Provider>
  );
});
FormItem.displayName = "FormItem";

const FormLabel = React.forwardRef(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      ref={ref}
      data-slot="form-label"
      data-error={!!error}
      className={cn(
        "data-[error=true]:text-red-500",
        className
      )}
      htmlFor={formItemId}
      {...props}
    />
  );
});
FormLabel.displayName = "FormLabel";

const FormControl = React.forwardRef(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return (
    <Slot
      ref={ref}
      data-slot="form-control"
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
});
FormControl.displayName = "FormControl";

const FormDescription = React.forwardRef(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();

  return (
    <div
      ref={ref}
      data-slot="form-description"
      id={formDescriptionId}
      className={cn("text-sm text-gray-500", className)}
      {...props}
    />
  );
});
FormDescription.displayName = "FormDescription";

const FormMessage = React.forwardRef(({
  className,
  children,
  ...props
}, ref) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? "") : children;

  if (!body) {
    return null;
  }

  // Check if className contains Font Awesome classes
  const isFontAwesomeClass = className && (className.includes('fas ') || className.includes('fa ') || className.includes('far ') || className.includes('fab '));

  return (
    <div
      ref={ref}
      data-slot="form-message"
      id={formMessageId}
      className={cn("text-sm", isFontAwesomeClass ? null : className)}
      style={{ color: "#ef4444" }}
      {...props}
    >
      {isFontAwesomeClass && (
        <i className={className} style={{ marginRight: '4px' }}></i>
      )}
      {body}
    </div>
  );
});
FormMessage.displayName = "FormMessage";

// Custom hook for form validation with field focusing
export const useFormValidation = (form, options = {}) => {
  const {
    showErrorsOnSubmit = true,
    focusOnFirstError = true,
    validateOnMount = false,
  } = options;

  const [showAllErrors, setShowAllErrors] = React.useState(false);
  const [focusedField, setFocusedField] = React.useState(null);

  // Reset error display state when form values change
  React.useEffect(() => {
    if (showAllErrors) {
      setShowAllErrors(false);
    }
  }, [form.watch()]);

  // Validate form and focus on first error
  const validateAndFocus = React.useCallback(async () => {
    const isValid = await form.trigger();

    if (!isValid) {
      // Show all errors when validation fails
      setShowAllErrors(true);

      if (focusOnFirstError) {
        const errors = form.formState.errors;
        const firstErrorField = Object.keys(errors)[0];

        if (firstErrorField) {
          setFocusedField(firstErrorField);

          // Focus on the field after a short delay to ensure DOM is updated
          setTimeout(() => {
            // Try multiple selectors to find the field element
            let fieldElement = document.querySelector(`[name="${firstErrorField}"]`);

            // If not found, try data-field-name attribute
            if (!fieldElement) {
              fieldElement = document.querySelector(`[data-field-name="${firstErrorField}"]`);
            }

            // If still not found, try to find the FormControlWithFocus element
            if (!fieldElement) {
              fieldElement = document.querySelector(`[data-focused-field="${firstErrorField}"]`);
            }

            // If still not found, try to find any element with the field name in its attributes
            if (!fieldElement) {
              fieldElement = document.querySelector(`[data-slot="form-control"][data-focused="true"]`);
            }

            if (fieldElement) {
              // For custom components, try to focus on the first focusable element within
              const focusableElement = fieldElement.querySelector('input, button, [tabindex]:not([tabindex="-1"])') || fieldElement;
              focusableElement.focus();
              focusableElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
          }, 100);
        }
      }
    }

    return isValid;
  }, [form, focusOnFirstError]);

  // Handle form submission with validation
  const handleSubmit = React.useCallback(async (onSubmit) => {
    if (showErrorsOnSubmit) {
      setShowAllErrors(true);
    }

    const isValid = await validateAndFocus();

    if (isValid) {
      return onSubmit();
    }

    return false;
  }, [validateAndFocus, showErrorsOnSubmit]);

  // Check if field should show error
  const shouldShowError = React.useCallback((fieldName) => {
    const hasError = !!form.formState.errors[fieldName];
    const isTouched = form.formState.touchedFields[fieldName];
    const isDirty = form.formState.dirtyFields[fieldName];
    const isSubmitted = form.formState.isSubmitted;

    // Show error if:
    // 1. Field has error AND (field is touched OR form has been submitted)
    // 2. OR if showAllErrors is true and field has error
    return hasError && (isTouched || isDirty || showAllErrors || isSubmitted);
  }, [form.formState.errors, form.formState.touchedFields, form.formState.dirtyFields, form.formState.isSubmitted, showAllErrors]);

  // Get field error message
  const getFieldError = React.useCallback((fieldName) => {
    const error = form.formState.errors[fieldName];
    return error?.message || '';
  }, [form.formState.errors]);

  // Check if field is currently focused
  const isFieldFocused = React.useCallback((fieldName) => {
    return focusedField === fieldName;
  }, [focusedField]);

  // Clear focused field
  const clearFocusedField = React.useCallback(() => {
    setFocusedField(null);
  }, []);

  return {
    validateAndFocus,
    handleSubmit,
    shouldShowError,
    getFieldError,
    isFieldFocused,
    clearFocusedField,
    showAllErrors,
    setShowAllErrors,
  };
};

// Enhanced FormMessage component that respects validation state
const FormMessageWithValidation = React.forwardRef(({
  className,
  children,
  fieldName,
  useValidation = true,
  iconClassName,
  ...props
}, ref) => {
  const { error, formMessageId } = useFormField();
  const { shouldShowError, getFieldError } = useFormValidation(useFormContext());

  // Use custom validation logic if fieldName is provided and useValidation is true
  const shouldShow = useValidation && fieldName
    ? shouldShowError(fieldName)
    : !!error;

  const errorMessage = useValidation && fieldName
    ? getFieldError(fieldName)
    : error?.message ?? "";

  const body = shouldShow ? errorMessage : children;

  if (!body) {
    return null;
  }

  // Check if className contains Font Awesome classes
  const isFontAwesomeClass = className && (className.includes('fas ') || className.includes('fa ') || className.includes('far ') || className.includes('fab '));

  return (
    <div
      ref={ref}
      data-slot="form-message"
      id={formMessageId}
      className={cn("text-sm", isFontAwesomeClass ? null : className)}
      style={{ color: "#ef4444" }}
      {...props}
    >
      {isFontAwesomeClass && (
        <i className={className} style={{ marginRight: '4px' }}></i>
      )}
      {body}
    </div>
  );
});
FormMessageWithValidation.displayName = "FormMessageWithValidation";

// Enhanced FormControl component with focus management
const FormControlWithFocus = React.forwardRef(({
  fieldName,
  onFocus,
  onBlur,
  ...props
}, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  const handleFocus = React.useCallback((e) => {
    if (onFocus) {
      onFocus(e);
    }
  }, [onFocus]);

  const handleBlur = React.useCallback((e) => {
    if (onBlur) {
      onBlur(e);
    }
  }, [onBlur]);

  return (
    <Slot
      ref={ref}
      data-slot="form-control"
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      data-field-name={fieldName}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...props}
    />
  );
});
FormControlWithFocus.displayName = "FormControlWithFocus";

/**
 * Handles generic input changes with optional sanitization
 * @param {Object} field - The form field object
 * @param {string} value - The input value
 * @param {Function} sanitizer - Optional sanitization function
 */
const handleInputChange = (field, value, sanitizer = null) => {
  const sanitizedValue = sanitizer ? sanitizer(value) : value;
  field.onChange(sanitizedValue);
};

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormControlWithFocus,
  FormDescription,
  FormMessage,
  FormMessageWithValidation,
  FormField,
  handleInputChange,
};